/**
 * Org-host routing helpers.
 *
 * Organisation dashboards normally live on <subdomain>.<rootDomain>. That does
 * not work on localhost or Vercel preview deployments (no wildcard DNS), so
 * those environments use path-based routing: /subdomain/<org>/...
 *
 * This file is the single source of truth for that choice. Import from here —
 * do not inline `.vercel.app` / localhost checks elsewhere.
 *
 * Two preview detectors:
 *  - isVercelPreviewHostname()  — server-side (pure function, no browser globals)
 *  - isVercelPreviewDeploy()    — client-side (reads window.location, call only in browser)
 */

const MAIN_DOMAIN =
  process.env.NEXT_PUBLIC_ROOT_DOMAIN || process.env.ROOT_DOMAIN;

/**
 * Returns true for a Vercel preview hostname that is NOT a subdomain of the
 * configured root domain.
 *
 * Safe to call from middleware and React Server Components.
 *
 * @example
 * isVercelPreviewHostname("detour-website-abc-software-mansion.vercel.app") // true
 * isVercelPreviewHostname("myorg.godetour.app")                              // false
 */
export function isVercelPreviewHostname(hostname: string): boolean {
  if (!MAIN_DOMAIN) return false;
  return (
    hostname.endsWith(".vercel.app") && !hostname.endsWith(`.${MAIN_DOMAIN}`)
  );
}

/**
 * Builds the root URL for an org dashboard, choosing the correct routing
 * strategy for the current environment:
 *  - Localhost / Vercel preview  → path-based:  https://<host>/subdomain/<org>/
 *  - Staging / prod              → subdomain:   https://<org>.<rootDomain>/
 *
 * Use this wherever middleware needs to redirect an authenticated user to their
 * organisation's dashboard root.
 */
export function buildOrgRootUrl(
  hostname: string,
  protocol: string,
  mainDomainWithPort: string,
  orgSubdomain: string | undefined
): string {
  const isLocalhost =
    mainDomainWithPort.includes("localhost") || hostname.includes("localhost");

  return isVercelPreviewHostname(hostname) || isLocalhost
    ? `${protocol}://${mainDomainWithPort}/subdomain/${orgSubdomain}/`
    : `${protocol}://${orgSubdomain}.${mainDomainWithPort}/`;
}

/**
 * Returns true when the browser is on a Vercel preview deployment that is NOT
 * a subdomain of the configured ROOT_DOMAIN.
 *
 * Safe to call only from Client Components and hooks (requires `window`).
 *
 * @example
 * // inside a hook / client component:
 * if (isVercelPreviewDeploy()) { /* use path-based org routing *‌/ }
 */
export function isVercelPreviewDeploy(): boolean {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  const rootDomainEnv = process.env.NEXT_PUBLIC_ROOT_DOMAIN || "";
  return (
    hostname.endsWith(".vercel.app") && !hostname.endsWith(`.${rootDomainEnv}`)
  );
}
